import classNames from 'classnames'
import { useState, useRef, useEffect } from 'react'
import { useTodos, useAddTodo, useUpdateTodo, useDeleteTodo, useToggleTodoStatus } from '../services/todo'

let TaskList = ({ className }) => {
    let [newTask, setNewTask] = useState("")
    let [editingTaskId, setEditingTaskId] = useState(null)
    let [editedTaskText, setEditedTaskText] = useState("")
    let inputRef = useRef(null)
    let editInputRef = useRef(null)
    let MAX_TODOS = 5

    let { data: todos = [], isLoading, error } = useTodos()

    // Mutations
    let addTodoMutation = useAddTodo();
    let updateTodoMutation = useUpdateTodo();
    let deleteTodoMutation = useDeleteTodo();
    let toggleTodoStatusMutation = useToggleTodoStatus();

    const handleAddTodo = async (e) => {
        if (e) e.preventDefault();
        if (!newTask.trim() || todos.length >= MAX_TODOS) return;

        try {
            await addTodoMutation.mutateAsync(newTask);
            setNewTask("");
        } catch (error) {
            console.error("Error adding todo:", error);
        }
    };

    const handleRemoveTodo = async (taskId) => {
        try {
            await deleteTodoMutation.mutateAsync(taskId);
        } catch (error) {
            console.error("Error removing todo:", error);
        }
    };

    const handleToggleTodoStatus = async (taskId, isChecked) => {
        try {
            await toggleTodoStatusMutation.mutateAsync({ taskId, isChecked });
        } catch (error) {
            console.error("Error toggling todo status:", error);
        }
    };

    const handleBlur = () => {
        if (newTask.trim()) {
            handleAddTodo();
        }
    };

    const handleStartEditing = (todo) => {
        setEditingTaskId(todo.id);
        setEditedTaskText(todo.task);
    };

    const handleUpdateTodo = async () => {
        if (!editingTaskId || !editedTaskText.trim()) {
            setEditingTaskId(null);
            return;
        }

        try {
            await updateTodoMutation.mutateAsync({
                taskId: editingTaskId,
                task: editedTaskText,
            });
            setEditingTaskId(null);
            setEditedTaskText("");
        } catch (error) {
            console.error("Error updating todo:", error);
        }
    };

    useEffect(() => {
        if (editingTaskId && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [editingTaskId])

    return <div className={classNames('flex flex-col gap-4 flex-1 self-start', className)}>
        <div className='text-3xl'>
            <strong>Let's</strong> <span className='font-black  bg-gradient-to-br from-primary to-main bg-clip-text text-transparent '>source</span>
            🚀
        </div>

        <div className='font-bold text-xl'>
            Deine Powerlist für heute
        </div>

        <div className="space-y-6">
            {todos.map((todo) => (
                <div key={todo.id} className="flex items-center group">
                    <button
                        onClick={() => handleToggleTodoStatus(todo.id, todo.checked)}
                        className="w-6 h-6 rounded flex items-center justify-center"
                        disabled={isLoading || editingTaskId === todo.id}>
                        {todo.checked ? (
                            <div className="w-6 h-6 rounded dark:bg-[#4AE3B5] bg-[#3B82FF] flex items-center justify-center">
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M10 3L4.5 8.5L2 6"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        ) : (
                            <div className="w-6 h-6 rounded border-2 dark:border-[#4AE3B5] border-[#3B82FF]" />
                        )}
                    </button>

                    {editingTaskId === todo.id ? (
                        <input
                            type="text"
                            ref={editInputRef}
                            value={editedTaskText}
                            onChange={(e) => setEditedTaskText(e.target.value)}
                            onBlur={handleUpdateTodo}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleUpdateTodo();
                                } else if (e.key === "Escape") {
                                    setEditingTaskId(null);
                                    setEditedTaskText("");
                                }
                            }}
                            className="ml-3 text-l bg-transparent outline-none border-none w-full"
                        />
                    ) : (
                        <span
                            className={`ml-3 ${todo.checked ? "line-through break-all text-gray-500" : ""
                                } cursor-pointer w-full`}
                            onClick={() => !todo.checked && handleStartEditing(todo)}>
                            {todo.task}
                        </span>
                    )}

                    <button
                        onClick={() => handleRemoveTodo(todo.id)}
                        className="ml-auto opacity-0 group-hover:opacity-100 text-red-500 transition-opacity"
                        disabled={isLoading || editingTaskId === todo.id}
                        aria-label="Delete task">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                    </button>
                </div>
            ))}

            {/* Empty slots for new tasks */}
            {todos.length < MAX_TODOS && (
                <form onSubmit={handleAddTodo} className="flex items-center">
                    <div className="w-6 h-6 rounded border-2 dark:border-[#4AE3B5] border-[#3B82FF]" />
                    <input
                        type="text"
                        ref={inputRef}
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        onBlur={handleBlur}
                        placeholder="Neue Aufgabe eintragen"
                        className="ml-3 bg-transparent outline-none border-none"
                        disabled={isLoading}
                    />
                    <button
                        type="submit"
                        className="ml-2 opacity-0 focus:opacity-100 hover:opacity-100 transition-opacity"
                        disabled={isLoading || !newTask}
                        aria-label="Add task">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="16"></line>
                            <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                    </button>
                </form>
            )}
        </div>
    </div>;
};

export default TaskList;